import { Title } from "@dropbox/dig-components/typography";
import { Box, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CheckmarkCircleFill,
  CircleStandardFilledFill,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { RichTextArea } from "@mirage/mosaics/BaseProfilePage/RichTextArea/RichTextArea";
import { useDebouncedValue } from "@mirage/mosaics/BaseProfilePage/RichTextArea/useDebouncedValue";
import {
  userInfoMapAtom,
  userProfileSettingsAtom,
} from "@mirage/mosaics/UserProfilePage/atoms";
import { UserProfileBackgroundImage } from "@mirage/mosaics/UserProfilePage/background/CustomizedBackground";
import {
  PROFILE_BACKGROUND_COLORS,
  PROFILE_BACKGROUND_THEMES,
  ProfileBackgroundColor,
} from "@mirage/mosaics/UserProfilePage/background/types";
import {
  getFillColors,
  getProfileBackgroundForEmail,
  getThemeBackgroundColors,
} from "@mirage/mosaics/UserProfilePage/background/utils";
import { formatDashGetPersonResponse } from "@mirage/mosaics/UserProfilePage/hooks";
import { UserProfileDetails } from "@mirage/mosaics/UserProfilePage/UserProfileDetails";
import { callApiV2 } from "@mirage/service-dbx-api";
import { showSnackbar } from "@mirage/shared/snackbar";
import i18n, { Trans } from "@mirage/translations";
import { useAtom, useSetAtom } from "jotai";
import { EditorState } from "lexical";
import { useEffect, useMemo, useRef, useState } from "react";

import type { ProfileBackgroundTheme } from "@mirage/mosaics/UserProfilePage/background/types";
import type {
  UserInfo,
  UserProfileSettings,
} from "@mirage/mosaics/UserProfilePage/types";
import styles from "./EditUserProfileBody.module.css";

interface EditProfileBodyProps {
  user: UserInfo;
}

const THEME_CONTAINER_WIDTH = 336;

export const EditUserProfileBody = ({ user }: EditProfileBodyProps) => {
  const setUserInfoMap = useSetAtom(userInfoMapAtom);
  const [userProfileSettings, setUserProfileSettings] = useAtom(
    userProfileSettingsAtom,
  );
  const [aboutMeEditorState, setAboutMeEditorState] = useState<EditorState>();
  const deboucedEditorState = useDebouncedValue<EditorState | undefined>(
    aboutMeEditorState,
    1000,
  );
  const aboutMeValue = useRef(user.user_profile_settings.about_me ?? undefined);
  const { backgroundColor, backgroundTheme } = getProfileBackgroundForEmail(
    user.email,
  );
  const selectedColor: ProfileBackgroundColor = user.user_profile_settings
    .profile_background_color
    ? (user.user_profile_settings
        .profile_background_color as ProfileBackgroundColor)
    : backgroundColor;
  const selectedTheme = user.user_profile_settings.profile_background_theme
    ? (user.user_profile_settings
        .profile_background_theme as ProfileBackgroundTheme)
    : backgroundTheme;
  const themeBackgroundColors = useMemo(() => {
    return getThemeBackgroundColors([selectedColor]);
  }, []);
  const { reportPapEvent } = useMirageAnalyticsContext();

  const handleEditAutoSave = async (
    newUserProfileSettings: UserProfileSettings,
  ) => {
    const resp = await callApiV2("dashPeopleUpdateProfileSettings", {
      user_profile_settings: newUserProfileSettings,
    });
    const newUserInfo = formatDashGetPersonResponse(resp.person);
    if (newUserInfo) {
      setUserProfileSettings(newUserInfo.user_profile_settings);
      setUserInfoMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(newUserInfo.display_handle, newUserInfo);
        return newMap;
      });

      reportPapEvent(
        PAP_Click_DashProfilePage({
          dashActionSurface: "dash_profile_page",
          dashProfileAction: "edit_save",
          featureLine: "people",
        }),
      );

      showSnackbar({ title: i18n.t("saved") });
    }
  };

  const handleThemeChange = (theme: ProfileBackgroundTheme) => {
    const newUserProfileSettings = {
      ...userProfileSettings,
      profile_background_theme: theme,
      profile_background_color: themeBackgroundColors[theme],
    };
    handleEditAutoSave(newUserProfileSettings);
  };

  const handleColorChange = (
    e: React.MouseEvent<SVGSVGElement>,
    color: ProfileBackgroundColor,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const newUserProfileSettings = {
      ...userProfileSettings,
      profile_background_color: color,
      profile_background_theme: selectedTheme,
    };
    handleEditAutoSave(newUserProfileSettings);
  };

  useEffect(() => {
    const aboutMeJson = deboucedEditorState?.toString() ?? null;
    if (aboutMeJson !== null && aboutMeJson !== userProfileSettings.about_me) {
      // Update about me setting in UI
      const newUserProfileSettings = {
        ...userProfileSettings,
        about_me: aboutMeJson,
      };
      setUserProfileSettings(newUserProfileSettings);

      // Update user profile setting in backend
      handleEditAutoSave(newUserProfileSettings);
    }
  }, [userProfileSettings, setUserProfileSettings, deboucedEditorState]);

  return (
    <Box className={styles.editProfileBody}>
      <Stack
        gap="32"
        style={{
          maxWidth: "704px",
        }}
      >
        <Stack gap="8">
          <Title size="small" weightVariant="emphasized" tagName="span">
            {i18n.t("user_profile_highlights_string")}
          </Title>
          <UserProfileDetails user={user} isEdit />
        </Stack>
        <Stack gap="8">
          <Title size="small" weightVariant="emphasized" tagName="span">
            {i18n.t("user_profile_about_me_string")}
          </Title>
          <RichTextArea
            source="EditUserProfilePage"
            editable
            topOffset={0}
            theme="small-faint"
            placeholder={
              <Trans
                i18nKey="user_profile_about_me_placeholder"
                components={{
                  tag: <span />,
                }}
              />
            }
            value={aboutMeValue.current}
            onChange={setAboutMeEditorState}
          />
        </Stack>
      </Stack>

      <Box className={styles.themeSection}>
        <Title size="small" weightVariant="emphasized" tagName="span">
          {i18n.t("user_profile_theme_string")}
        </Title>
        <Stack gap="16" style={{ marginTop: "12px" }}>
          {PROFILE_BACKGROUND_THEMES.map((theme) => {
            const isSelectedTheme = theme === selectedTheme;
            return (
              <Box
                key={`edit-user-profile-page-theme__${theme}`}
                borderRadius="Large"
                boxShadow="Raised"
                cursor="pointer"
                overflow="hidden"
                style={{ width: THEME_CONTAINER_WIDTH }}
                onClick={() => handleThemeChange(theme)}
              >
                <UserProfileBackgroundImage
                  color={
                    isSelectedTheme
                      ? selectedColor
                      : themeBackgroundColors[theme]
                  }
                  theme={theme}
                  height="64px"
                  width={`${THEME_CONTAINER_WIDTH}px`}
                />
                {isSelectedTheme && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor={
                      selectedColor === "white"
                        ? "Background Raised"
                        : "Background Subtle"
                    }
                    style={{ height: "48px", marginTop: "-10px" }}
                  >
                    {PROFILE_BACKGROUND_COLORS.map((color) => {
                      const isSelectedColor = color === selectedColor;
                      const { svgFillColor } = getFillColors(color);
                      return (
                        <UIIcon
                          key={`edit-user-profile-page-color__${color}`}
                          size="large"
                          color={svgFillColor}
                          src={
                            isSelectedColor
                              ? CheckmarkCircleFill
                              : CircleStandardFilledFill
                          }
                          onClick={(e: React.MouseEvent<SVGSVGElement>) =>
                            handleColorChange(e, color)
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};
